import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { RxCross2 } from 'react-icons/rx';
import Toggle from 'react-toggle';
import { getCustomerInfo, updateCustomerStatus } from 'app/api/customer';
import HeaderWithBackButton from 'app/components/HeaderWithBackButton';
import NewInput from 'app/components/NewInput';
import Button from 'app/components/button';
import PageLoader from 'app/components/PageLoader';
import { VOUCHER_PASSWORD } from 'app/constants/api-contants';
import { APP_CONSTANTS, LocalSessionKey } from 'app/constants/app-constants';
import Message from 'app/utils/Message';
import Toast from 'app/utils/Toast';
import { LocalStorage } from 'app/utils/storage';
import CustomerBillHistory from './CustomerBillHistory';
import CustomerInfo from './CustomerInfo';
import CustomerLedger from './CustomerLedger';
import CustomerMallWiseTier from './CustomerMallWiseTier';
import CustomerPrivilege from './CustomerPrivilege';
import CustomerVouchers from './CustomerVouchers';

const CustomerDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Capture the list URL once on mount. The tab tables call setSearchParams,
  // which navigates without state and wipes location.state — so we can't read
  // location.state.listUrl at back-click time; the ref preserves it.
  const listUrlRef = useRef(location.state?.listUrl);
  const customerData = JSON.parse(
    LocalStorage.get(LocalSessionKey.CUSTOMER_DATA)
  );

  const [pageLoading, setPageLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('contactInfo');
  const [latestDeletionDate, setLatestDeletionDate] = useState(null);
  const [latestRestorationDate, setLatestRestorationDate] = useState(null);

  const [passwordmodelOpen, setPasswordmodelOpen] = useState(false);
  const [passwordVal, setPasswordVal] = useState('');
  const [pendingData, setPendingData] = useState(null);

  useEffect(() => {
    if (!customerData?.id) return;
    let cancelled = false;
    (async () => {
      const response = await getCustomerInfo(customerData.id);
      if (cancelled) return;
      if (response?.responseCode === 200) {
        if (response?.data?.latestDeletionDate) {
          setLatestDeletionDate(response.data.latestDeletionDate);
        }
        if (response?.data?.latestRestorationDate) {
          setLatestRestorationDate(response.data.latestRestorationDate);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const tabContent = {
    contactInfo: <CustomerInfo id={customerData?.id} />,
    billHistory: <CustomerBillHistory latestDeletionDate={latestDeletionDate} latestRestorationDate={latestRestorationDate} />,
    vouchers: <CustomerVouchers latestDeletionDate={latestDeletionDate} latestRestorationDate={latestRestorationDate} />,
    mallWiseTier: <CustomerMallWiseTier latestDeletionDate={latestDeletionDate} latestRestorationDate={latestRestorationDate} />,
    privilegesHistory: <CustomerPrivilege latestDeletionDate={latestDeletionDate} latestRestorationDate={latestRestorationDate} />,
    customerLedger: <CustomerLedger latestDeletionDate={latestDeletionDate} latestRestorationDate={latestRestorationDate} />,
  };

  const handleStatusChange = (data) => {
    setPendingData(data);
    setPasswordmodelOpen(true);
  };

  const _executeStatusChange = async (data) => {
    const newStatus = data?.status === 3 ? 1 : 3;
    setPageLoading(true);
    const response = await updateCustomerStatus(data?.id, newStatus);
    setPageLoading(false);

    if (response?.responseCode === 200) {
      Toast.success(
        newStatus === 3
          ? Message.Customer.STATUS_BLOCKED_SUCCESS
          : Message.Customer.STATUS_UNBLOCKED_SUCCESS
      );
      LocalStorage.set(LocalSessionKey.CUSTOMER_DATA, response?.data);

      navigate(`/customers/details/${response?.data?.id}`, {
        state: {
          ...response.data,
          // Preserve the list URL across the status-change re-navigation so the
          // back button still returns to the right list page/search.
          listUrl: location.state?.listUrl,
        },
      });
    } else {
      Toast.error(
        response?.response?.message || Message.Customer.STATUS_UPDATED_FAILURE
      );
    }
  };

  const handlemodelvalues = () => {
    setPasswordmodelOpen(false);

    if (passwordVal && passwordVal !== VOUCHER_PASSWORD.VOUCHER_PASSWORD) {
      Toast.error('Your Entered Password is wrong.');
      setPasswordVal('');
      return;
    }
    setPasswordVal('');
    _executeStatusChange(pendingData);
  };

  const closeModal = () => {
    setPasswordmodelOpen(false);
    setPasswordVal('');
  };

  const modalContent = useMemo(
    () => (
      <Modal
        size='sm'
        show={passwordmodelOpen}
        onHide={closeModal}
        aria-labelledby='password-popup'
        centered
      >
        <div className='cursor-pointer'>
          <span className='close-icon' onClick={closeModal}>
            <RxCross2 />
          </span>
        </div>
        <Modal.Body className='text-center d-flex justify-content-around mt-5'>
          <div className='modal-image-form'>
            <NewInput
              label='Password*'
              type='text'
              placeholder='Enter Password'
              value={passwordVal}
              onChange={(e) => setPasswordVal(e.target.value)}
            />
            <Button
              className='mt1 button ms-2'
              onClick={handlemodelvalues}
              variant='Submit'
              name='Submit'
              isDisabled={!passwordVal}
            />
          </div>
        </Modal.Body>
      </Modal>
    ),
    [passwordmodelOpen, handlemodelvalues, passwordVal]
  );

  return (
    <>
      <div className='cms-content__container'>
        <div className='flex justify-content-between'>
          <HeaderWithBackButton
            title={`Customer Details View`}
            // Return to the exact list URL we came from (preserving its page +
            // search), instead of navigate(-1) which walks back through the Bill
            // History ?page= history. Falls back to the list root on refresh.
            onBackClicked={() =>
              navigate(listUrlRef.current || APP_CONSTANTS.CUSTOMER_MANAGEMENT)
            }
          />
          <div className='flex'>
            <span
              className={
                customerData?.status === 1 ? 'active bdg' : 'inactive bdg'
              }
            >
              {customerData?.status === 1 ? 'Unblocked' : 'Blocked'}
            </span>
            <Toggle
              className='mx-3'
              defaultChecked={customerData?.status === 3 ? true : false}
              icons={false}
              onChange={() => handleStatusChange(customerData)}
            />
          </div>
        </div>

        <div className='tabs'>
          <button
            onClick={() => handleTabClick('contactInfo')}
            className={activeTab === 'contactInfo' ? 'active' : ''}
          >
            Contact Information
          </button>
          <button
            onClick={() => handleTabClick('billHistory')}
            className={activeTab === 'billHistory' ? 'active' : ''}
          >
            Bill History
          </button>
          <button
            onClick={() => handleTabClick('vouchers')}
            className={activeTab === 'vouchers' ? 'active' : ''}
          >
            Vouchers
          </button>
          <button
            onClick={() => handleTabClick('mallWiseTier')}
            className={activeTab === 'mallWiseTier' ? 'active' : ''}
          >
            Mall Wise Tier
          </button>
          <button
            onClick={() => handleTabClick('privilegesHistory')}
            className={activeTab === 'privilegesHistory' ? 'active' : ''}
          >
            Privileges History
          </button>
          <button
            onClick={() => handleTabClick('customerLedger')}
            className={activeTab === 'customerLedger' ? 'active' : ''}
          >
            Customer Ledger
          </button>
        </div>

        <div className='tab-content'>{tabContent[activeTab]}</div>
      </div>
      {modalContent}
      {pageLoading === true && <PageLoader pageLoading={pageLoading} />}
    </>
  );
};

export default CustomerDetail;
